import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Cancel as CancelIcon,
  PauseCircle as PauseCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Info as InfoIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Composant externe pour afficher les notifications de statut (rejected/inactive)
 * et les modals de détail associés dans le formulaire d'édition
 * 
 * @param {Object} props
 * @param {Object} props.listing - L'objet listing contenant le statut et les informations
 */
const ListingStatusNotification = ({ listing }) => {
  const { t } = useTranslation();
  
  const [rejectionPopupOpen, setRejectionPopupOpen] = useState(false);
  const [deactivationPopupOpen, setDeactivationPopupOpen] = useState(false);

  if (!listing) return null;

  const isRejected = listing.status === 'rejected';
  const isInactive = listing.status === 'inactive';

  // Si le statut n'est ni rejected ni inactive, ne rien afficher
  if (!isRejected && !isInactive) return null;

  return (
    <>
      {/* Notification de rejet */}
      {isRejected && (
        <Card
          sx={{ 
            mb: 3,
            border: '2px solid #f44336',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffebee 100%)',
            position: 'relative',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 36px rgba(244, 67, 54, 0.25)',
            },
          }}
          onClick={() => setRejectionPopupOpen(true)}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <CancelIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('listing.detail.rejectionNotice.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('listing.form.rejectionNotice.editDescription')}
              </Typography>
            </Box>
            
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              animation: 'pulse 1.5s infinite'
            }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 'medium' }}>
                {t('listing.form.rejectionNotice.viewReason')}
              </Typography>
              <ExpandMoreIcon sx={{ 
                fontSize: 24,
                animation: 'bounce 1s infinite alternate',
                '@keyframes bounce': {
                  '0%': { transform: 'translateY(0)' },
                  '100%': { transform: 'translateY(5px)' }
                }
              }} />
            </Box>
            
            <style jsx>{`
              @keyframes pulse {
                0% { opacity: 0.7; }
                50% { opacity: 1; }
                100% { opacity: 0.7; }
              }
            `}</style>
          </Box>
        </Card>
      )}

      {/* Notification de désactivation */}
      {isInactive && (
        <Card
          sx={{ 
            mb: 3,
            border: '2px solid #ff9800',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(255, 152, 0, 0.15)',
            background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
            position: 'relative',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 36px rgba(255, 152, 0, 0.25)',
            },
          }}
          onClick={() => setDeactivationPopupOpen(true)}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <PauseCircleIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('listing.detail.deactivationNotice.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('listing.form.deactivationNotice.editDescription')}
              </Typography>
            </Box>
            
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              animation: 'pulse 1.5s infinite'
            }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 'medium' }}>
                {t('listing.form.deactivationNotice.viewReason')}
              </Typography>
              <ExpandMoreIcon sx={{ 
                fontSize: 24,
                animation: 'bounce 1s infinite alternate',
                '@keyframes bounce': {
                  '0%': { transform: 'translateY(0)' },
                  '100%': { transform: 'translateY(5px)' }
                }
              }} />
            </Box>
            
            <style jsx>{`
              @keyframes pulse {
                0% { opacity: 0.7; }
                50% { opacity: 1; }
                100% { opacity: 0.7; }
              }
            `}</style>
          </Box>
        </Card>
      )}

      {/* Modal de détail du rejet */}
      <Dialog 
        open={rejectionPopupOpen} 
        onClose={() => setRejectionPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 500
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          p: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CancelIcon sx={{ mr: 2, fontSize: 36 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('listing.form.rejectionNotice.dialogTitle')}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ opacity: 0.9, ml: 0.5 }}>
            {t('listing.form.rejectionNotice.dialogDescription')}
          </Typography>
        </Box>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ 
              color: '#d32f2f', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}>
              <WarningIcon sx={{ mr: 1 }} />
              {t('listing.detail.rejectionReason')}
            </Typography>
            
            <Paper elevation={0} sx={{
              p: 3,
              backgroundColor: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: 2,
              borderLeft: '4px solid #ff9800',
              mb: 3,
              position: 'relative',
              animation: 'fadeIn 0.6s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 'medium',
                color: '#e65100',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {listing.rejection_reason ||
                  t('listing.detail.defaultRejectionMessage')}
              </Typography>
            </Paper>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              mt: 3
            }}>
              <InfoIcon sx={{ mr: 1 }} />
              {t('listing.form.rejectionNotice.howToCorrect')}
            </Typography>
            
            <List dense>
              {[
                t('listing.form.rejectionNotice.step1'),
                t('listing.form.rejectionNotice.step2'),
                t('listing.form.rejectionNotice.step3')
              ].map((text, index) => (
                <ListItem key={index} sx={{ 
                  pl: 0,
                  animation: `slideIn 0.5s ${index * 0.2}s forwards`,
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  '@keyframes slideIn': {
                    '0%': { opacity: 0, transform: 'translateX(-10px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' }
                  }
                }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}>
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={text}
                    primaryTypographyProps={{
                      sx: { fontWeight: 'medium' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => window.open('mailto:support@krillou.com?subject=Question sur le rejet de mon annonce', '_blank')}
            startIcon={<EmailIcon />}
            sx={{
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            {t('common.contactSupport')}
          </Button>

          <Button
            onClick={() => setRejectionPopupOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #6200ee 0%, #03dac4 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #03dac4 0%, #6200ee 100%)',
              }
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de détail de la désactivation */}
      <Dialog 
        open={deactivationPopupOpen} 
        onClose={() => setDeactivationPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 500
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          p: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PauseCircleIcon sx={{ mr: 2, fontSize: 36 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {t('listing.form.deactivationNotice.dialogTitle')}
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ opacity: 0.9, ml: 0.5 }}>
            {t('listing.form.deactivationNotice.dialogDescription')}
          </Typography>
        </Box>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ 
              color: '#f57c00', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}>
              <BlockIcon sx={{ mr: 1 }} />
              {t('listing.detail.deactivationReason')}
            </Typography>
            
            <Paper elevation={0} sx={{
              p: 3,
              backgroundColor: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: 2,
              borderLeft: '4px solid #ff9800',
              mb: 3,
              position: 'relative',
              animation: 'fadeIn 0.6s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}>
              <Typography variant="body1" sx={{ 
                fontWeight: 'medium',
                color: '#e65100',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {listing.rejection_reason ||
                  t('listing.detail.defaultDeactivationMessage')}
              </Typography>
            </Paper>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              mt: 3
            }}>
              <InfoIcon sx={{ mr: 1 }} />
              {t('listing.form.deactivationNotice.howToCorrect')}
            </Typography>
            
            <List dense>
              {[
                t('listing.form.deactivationNotice.step1'),
                t('listing.form.deactivationNotice.step2'),
                t('listing.form.deactivationNotice.step3')
              ].map((text, index) => (
                <ListItem key={index} sx={{ 
                  pl: 0,
                  animation: `slideIn 0.5s ${index * 0.2}s forwards`,
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  '@keyframes slideIn': {
                    '0%': { opacity: 0, transform: 'translateX(-10px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' }
                  }
                }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}>
                      {index + 1}
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={text}
                    primaryTypographyProps={{
                      sx: { fontWeight: 'medium' }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => window.open('mailto:support@krillou.com?subject=Question sur la désactivation de mon annonce', '_blank')}
            startIcon={<EmailIcon />}
            sx={{
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            {t('common.contactSupport')}
          </Button>

          <Button
            onClick={() => setDeactivationPopupOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #6200ee 0%, #03dac4 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #03dac4 0%, #6200ee 100%)',
              }
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListingStatusNotification;
